import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useTranslation } from 'react-i18next'
import ReactMarkdown from 'react-markdown'
import { Loader2, MessageSquarePlus, Send, Sparkles, Trash2, X } from 'lucide-react'
import { Card, CardContent } from '../../../components/ui/card'
import { supabase } from '../../../lib/supabaseClient'
import { useShowAiProvider } from '../../../hooks/useShowAiProvider'
import { cn } from '../../../lib/utils'

// Per-article AI chat. Renders as a collapsed CTA at the top of the
// article body; tapping expands a mini chat panel. NO LLM calls
// happen until the user types and submits — quota-conscious by
// design.
//
// Multi-turn, client-side history only. Each turn sends the full
// message array; the edge function prepends the article body as
// system context.

const SUGGESTIONS_AR = [
  'لخّص لى المقال',
  'ايه أهم النقاط؟',
  'في إيه نقد على المحتوى؟',
  'ايه الاستنتاج الرئيسى؟',
]
const SUGGESTIONS_EN = [
  'Summarise this article',
  'What are the key points?',
  'Any critical takeaways?',
  'What\'s the main conclusion?',
]

export default function ArticleChat({ articleId }) {
  const { t, i18n } = useTranslation()
  const isAr = i18n.language === 'ar'
  const [open, setOpen] = useState(false)
  const [question, setQuestion] = useState('')
  const [messages, setMessages] = useState([])
  const [pending, setPending] = useState(false)
  const [error, setError] = useState(null)
  const inputRef = useRef(null)
  const scrollRef = useRef(null)
  const { enabled: showProvider } = useShowAiProvider()

  // Reset state when the article changes — closing + clearing the
  // history so opening another article doesn't show stale messages.
  useEffect(() => {
    setMessages([])
    setQuestion('')
    setError(null)
    setOpen(false)
  }, [articleId])

  // Auto-scroll on new messages or pending state changes.
  useEffect(() => {
    const el = scrollRef.current
    if (!el) return
    el.scrollTop = el.scrollHeight
  }, [messages, pending])

  // Focus the input when the panel opens.
  useEffect(() => {
    if (!open) return
    const r = requestAnimationFrame(() => inputRef.current?.focus())
    return () => cancelAnimationFrame(r)
  }, [open])

  const handleAsk = useCallback(async (q) => {
    const text = (q ?? question).trim()
    if (!text || pending || !articleId) return
    setQuestion('')
    setError(null)
    const nextMessages = [...messages, { role: 'user', content: text }]
    setMessages(nextMessages)
    setPending(true)
    try {
      const { data, error: err } = await supabase.functions.invoke('article-chat', {
        body: { article_id: articleId, messages: nextMessages, language: isAr ? 'ar' : 'en' },
      })
      if (err) throw err
      if (data?.error) setError(data.error)
      else if (data?.answer) {
        setMessages((prev) => [...prev, { role: 'assistant', content: data.answer, provider: data.provider }])
      } else {
        setError('Empty response')
      }
    } catch (e) {
      setError(e?.message || 'failed')
    } finally {
      setPending(false)
    }
  }, [question, pending, articleId, messages, isAr])

  const handleSubmit = (e) => {
    e.preventDefault()
    handleAsk()
  }
  const handleClear = () => { setMessages([]); setError(null) }
  const suggestions = isAr ? SUGGESTIONS_AR : SUGGESTIONS_EN

  // Collapsed CTA — single tap to expand.
  if (!open) {
    return (
      <button
        type="button"
        onClick={() => setOpen(true)}
        className="w-full inline-flex items-center gap-2 px-3 py-2.5 rounded-lg bg-app-primary-500/10 hover:bg-app-primary-500/15 text-app-primary-500 text-sm font-semibold transition-colors"
        dir="auto"
      >
        <Sparkles className="w-4 h-4" />
        <span>{t('articleChat.cta')}</span>
        <MessageSquarePlus className="w-4 h-4 ms-auto" />
      </button>
    )
  }

  return (
    <Card className="overflow-hidden border-app-primary-500/30">
      <CardContent className="p-3 space-y-3">
        {/* Header */}
        <div className="flex items-center gap-2">
          <Sparkles className="w-4 h-4 text-app-primary-500" />
          <h3 className="text-sm font-semibold text-foreground flex-1" dir="auto">
            {t('articleChat.title')}
          </h3>
          {messages.length > 0 && (
            <button
              type="button"
              onClick={handleClear}
              aria-label={t('assistant.clearChat')}
              title={t('assistant.clearChat')}
              className="p-1 rounded-md text-muted-foreground hover:bg-accent"
            >
              <Trash2 className="w-3.5 h-3.5" />
            </button>
          )}
          <button
            type="button"
            onClick={() => setOpen(false)}
            aria-label={t('common.hide')}
            className="p-1 rounded-md text-muted-foreground hover:bg-accent"
          >
            <X className="w-4 h-4" />
          </button>
        </div>

        {/* Messages */}
        {(messages.length > 0 || pending) && (
          <div
            ref={scrollRef}
            className="max-h-[40vh] overflow-y-auto space-y-2 -mx-1 px-1"
          >
            {messages.map((m, idx) => (
              <Bubble key={idx} message={m} isAr={isAr} showProvider={showProvider} />
            ))}
            {pending && (
              <div className="inline-flex items-center gap-2 text-xs text-muted-foreground py-1.5 px-3 rounded-2xl bg-muted/60" dir="auto">
                <Loader2 className="w-3 h-3 animate-spin" />
                <span>{t('assistant.thinking')}</span>
              </div>
            )}
          </div>
        )}

        {/* Suggestions row — only when empty. */}
        {messages.length === 0 && !pending && (
          <div className="flex flex-wrap gap-1.5">
            {suggestions.map((s) => (
              <button
                key={s}
                type="button"
                onClick={() => handleAsk(s)}
                className="text-[11px] px-2.5 py-1.5 rounded-full bg-muted hover:bg-accent text-foreground"
                dir="auto"
              >
                {s}
              </button>
            ))}
          </div>
        )}

        {error && (
          <p className="text-xs text-red-600 px-2 py-1.5 rounded bg-red-500/10" dir="auto">
            {t('assistant.error', { error })}
          </p>
        )}

        {/* Composer */}
        <form onSubmit={handleSubmit} className="flex items-end gap-2">
          <input
            ref={inputRef}
            type="text"
            value={question}
            onChange={(e) => setQuestion(e.target.value)}
            placeholder={t('articleChat.placeholder')}
            disabled={pending}
            className="flex-1 h-10 px-3 rounded-md border border-input bg-background text-sm text-foreground disabled:opacity-60"
          />
          <button
            type="submit"
            disabled={pending || !question.trim()}
            aria-label={t('assistant.send')}
            className={cn(
              'h-10 w-10 rounded-md inline-flex items-center justify-center transition-colors flex-shrink-0',
              question.trim() && !pending
                ? 'bg-app-primary-500 text-white hover:bg-app-primary-500/90'
                : 'bg-muted text-muted-foreground',
            )}
          >
            {pending ? (
              <Loader2 className="w-4 h-4 animate-spin" />
            ) : (
              <Send className="w-4 h-4 rtl:-scale-x-100" />
            )}
          </button>
        </form>
      </CardContent>
    </Card>
  )
}

function Bubble({ message, isAr, showProvider }) {
  const isUser = message.role === 'user'
  return (
    <div className={cn('flex', isUser ? 'justify-end' : 'justify-start')}>
      <div
        className={cn(
          'max-w-[85%] rounded-2xl px-3 py-2 text-sm leading-relaxed',
          isUser
            ? 'bg-app-primary-500 text-white rounded-br-sm'
            : 'bg-muted text-foreground rounded-bl-sm',
        )}
        dir={isAr ? 'rtl' : 'ltr'}
      >
        {isUser ? (
          <p className="whitespace-pre-wrap">{message.content}</p>
        ) : (
          <div className="prose prose-sm dark:prose-invert max-w-none">
            <ReactMarkdown>{message.content}</ReactMarkdown>
          </div>
        )}
        {message.provider && !isUser && showProvider && (
          <p className="text-[9px] text-muted-foreground/70 font-mono mt-1" dir="ltr">
            via {message.provider}
          </p>
        )}
      </div>
    </div>
  )
}
